import { ref } from 'vue'

import type { SelectedCustomizationQuantities } from '~/types/SelectedCustomizationQuantities'

/** The subset of a customization group the selection rules depend on. */
interface CustomizationSelectionRules {
  readonly id: number
  readonly allow_repeated_selection?: boolean | undefined
  readonly max_per_option: number
  readonly max_selections: number
  readonly max_total_selections?: number | undefined
}

/** Quantity selection and caps for a menu item's customization groups. */
export const useCustomizationSelection = () => {
  const quantities = ref<SelectedCustomizationQuantities>({})

  const getOptionQuantity = (customizationId: number, optionId: number) =>
    quantities.value[customizationId]?.[optionId] ?? 0

  const getCustomizationSelectionCount = (customizationId: number) =>
    Object.values(quantities.value[customizationId] ?? {}).reduce((total, quantity) => {
      return quantity > 0 ? total + quantity : total
    }, 0)

  const getCustomizationUniqueSelectionCount = (customizationId: number) =>
    Object.values(quantities.value[customizationId] ?? {}).filter((quantity) => quantity > 0).length

  const allowsRepeatedSelection = (customization: CustomizationSelectionRules) =>
    Boolean(customization.allow_repeated_selection ?? customization.max_per_option > 1)

  /** `Infinity` when the group has no total cap (unset). */
  const getMaxTotalSelections = (customization: CustomizationSelectionRules) =>
    customization.max_total_selections ?? Infinity

  const setOptionQuantity = (customizationId: number, optionId: number, quantity: number) => {
    if (!quantities.value[customizationId]) {
      quantities.value[customizationId] = {}
    }

    if (quantity <= 0) {
      const currentSelections = quantities.value[customizationId]
      const { [optionId]: _removed, ...restSelections } = currentSelections
      quantities.value[customizationId] = restSelections
      return
    }

    quantities.value[customizationId][optionId] = quantity
  }

  const selectSingleOption = (customizationId: number, optionId: number) => {
    quantities.value[customizationId] = { [optionId]: 1 }
  }

  const hasReachedTotalSelectionLimit = (customization: CustomizationSelectionRules) => {
    if (!allowsRepeatedSelection(customization)) return false

    const maxTotalSelections = getMaxTotalSelections(customization)
    return (
      Number.isFinite(maxTotalSelections) &&
      getCustomizationSelectionCount(customization.id) >= maxTotalSelections
    )
  }

  // An unselected option cannot join once the group already holds max_selections distinct options.
  const hasReachedDistinctOptionLimit = (
    customization: CustomizationSelectionRules,
    currentQuantity: number
  ) =>
    currentQuantity === 0 &&
    customization.max_selections > 0 &&
    getCustomizationUniqueSelectionCount(customization.id) >= customization.max_selections

  const isOptionQuantityValid = (customization: CustomizationSelectionRules, optionId: number) =>
    getOptionQuantity(customization.id, optionId) <= customization.max_per_option

  const canSelectOption = (
    customization: CustomizationSelectionRules,
    optionId: number,
    isAvailable = true
  ) => {
    const currentQuantity = getOptionQuantity(customization.id, optionId)
    const hasSelectedQuantity = currentQuantity > 0
    if (hasSelectedQuantity) return true

    return (
      isAvailable &&
      customization.max_per_option >= 1 &&
      !hasReachedTotalSelectionLimit(customization) &&
      !hasReachedDistinctOptionLimit(customization, currentQuantity)
    )
  }

  const canIncrementOption = (
    customization: CustomizationSelectionRules,
    optionId: number,
    isAvailable = true
  ) => {
    const currentQuantity = getOptionQuantity(customization.id, optionId)
    const hasPerOptionCapacity = currentQuantity < customization.max_per_option

    return (
      isAvailable &&
      hasPerOptionCapacity &&
      !hasReachedTotalSelectionLimit(customization) &&
      !hasReachedDistinctOptionLimit(customization, currentQuantity)
    )
  }

  const toggleOption = (
    customization: CustomizationSelectionRules,
    optionId: number,
    isAvailable = true
  ) => {
    if (!canSelectOption(customization, optionId, isAvailable)) return

    const currentQuantity = getOptionQuantity(customization.id, optionId)
    const hasSelectedQuantity = currentQuantity > 0
    setOptionQuantity(customization.id, optionId, hasSelectedQuantity ? 0 : 1)
  }

  const incrementOption = (
    customization: CustomizationSelectionRules,
    optionId: number,
    isAvailable = true
  ) => {
    if (!canIncrementOption(customization, optionId, isAvailable)) return

    const currentQuantity = getOptionQuantity(customization.id, optionId)
    setOptionQuantity(customization.id, optionId, currentQuantity + 1)
  }

  const decrementOption = (customizationId: number, optionId: number) => {
    const currentQuantity = getOptionQuantity(customizationId, optionId)
    setOptionQuantity(customizationId, optionId, currentQuantity - 1)
  }

  const resetQuantities = (customizationIds: readonly number[]) => {
    quantities.value = Object.fromEntries(customizationIds.map((id) => [id, {}]))
  }

  return {
    allowsRepeatedSelection,
    decrementOption,
    getCustomizationSelectionCount,
    getCustomizationUniqueSelectionCount,
    getMaxTotalSelections,
    getOptionQuantity,
    incrementOption,
    canSelectOption,
    canIncrementOption,
    isOptionQuantityValid,
    quantities,
    resetQuantities,
    selectSingleOption,
    setOptionQuantity,
    toggleOption
  }
}
